/* eslint-disable no-undef */
import {BrowserRouter, Routes, Route} from 'react-router-dom'
import {useEffect} from 'react'
import {Provider} from 'react-redux'
import {GoogleReCaptchaProvider} from 'react-google-recaptcha-v3'
import {createAppStore} from '../store'
import {StyleReset} from './style_reset'
import {Helmet} from 'react-helmet-async'
import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'
import '../lib/i18n'
import {Env, DeployStage} from '../env'
import 'react-big-calendar/lib/css/react-big-calendar.css'
import 'react-datepicker/dist/react-datepicker.css'
// import 'react-calendar/dist/Calendar.css'
import {
  ProtectedRoute,
  Navbar,
  SignupPage,
  LoginPage,
  ProjectLandingPage,
  AccountSetupPage,
  ProjectSetupPage,
  ParticipantsPage,
  AdherencePage,
  AdherenceTableBlock,
  DataVisualizerPage,
  DataVisualizerTimeseriesPage,
  QuestionnairePage,
  Loading,
  PageNotFoundPage,
  ConnectionNoticeBanner,
  ProjectSettingsPage,
  AnalysisPage,
  OverviewPageV2,
  DataDownloadPage,
} from '.'

import {ErrorBoundaries} from './error_boundaries'
import {API_URL} from '../lib'
import {useAutoLogoutTracker} from '../lib/custom_hooks/useAutoLogoutTracker'

const trustedSourceList = [
  'https://*.hotjar.com',
  'https://faro-collector-prod-us-east-0.grafana.net',
  'https://s-d-pomelo-bin.s3.us-east-1.amazonaws.com',
  'https://s-d-pomelo-tabular-data.s3.us-east-1.amazonaws.com',
  'https://s-s-pomelo-tabular-data.s3.us-east-1.amazonaws.com',
  'https://r-r-pomelo-tabular-data.s3.us-east-1.amazonaws.com',
  'https://fonts.gstatic.com',
  'https://develop.api.pomelo.click',
  'https://staging.api.pomelo.click',
  'https://api.labfront.com',
  'http://localhost:9000',
  'https://cdnjs.cloudflare.com',
  'https://www.google.com',
  'http://127.0.0.1:8001',
  'http://127.0.0.1:80',
  API_URL,
]

const trackers = [
  'https://*.hotjar.com',
  'https://*.hotjar.io',
  'https://faro-collector-prod-us-east-0.grafana.net',
  'https://labfront.instatus.com',
  'https://www.google.com',
  'https://www.gstatic.com',
  'https://api.instatus.com',
  'https://cdnjs.cloudflare.com/ajax/libs/mathjax/3.2.2/es5/tex-mml-chtml.js',
]

const trustedImageSrcList = [
  'https://s-d-pomelo-bin.s3.us-east-1.amazonaws.com',
  'https://img.youtube.com',
  'https://develop.bin.pomelo.click',
  'https://staging.bin.pomelo.click',
  'https://asset.labfront.com',
  'https://app.labfront.com/favicon.ico',
  'data:',
]
const trustedFrameSrcList = ['https://www.google.com', 'https://labfront.instatus.com']
const trustedFontSrcList = ['https://fonts.gstatic.com', 'https://fonts.googleapis.com', 'https://cdnjs.cloudflare.com']
const trustedScriptSrcList = [].concat(trackers)
const trustedConnectSrcList = [
  'https://s-d-pomelo-bin.s3.us-east-1.amazonaws.com',
  'https://s-s-pomelo-bin.s3.us-east-1.amazonaws.com',
  'https://r-r-pomelo-bin.s3.us-east-1.amazonaws.com',
  'https://s-d-pomelo-tabular-data.s3.us-east-1.amazonaws.com',
  'https://s-s-pomelo-tabular-data.s3.us-east-1.amazonaws.com',
  'https://r-r-pomelo-tabular-data.s3.us-east-1.amazonaws.com',
  'https://develop.api.pomelo.click',
  'https://staging.api.pomelo.click',
  'https://api.labfront.com',
  'wss://ws.hotjar.com',
  'http://127.0.0.1:8001',
  'http://127.0.0.1:80',
].concat(trackers)
const trustedStyleSrcList = [].concat(trustedFontSrcList)

export const AutoTimeoutTracker = ({timeoutMinutes = 30}) => {
  useAutoLogoutTracker({timeoutMinutes})
  return null
}

export const App = () => {
  useEffect(() => {
    const webUrl = window.location.href
    if (Env.shared.stageIs(DeployStage.Develop)) document.title = 'LF-Develop'
    if (Env.shared.stageIs(DeployStage.Staging)) document.title = 'LF-Staging'
    if (webUrl.slice(0, 32) === 'https://release.app.labfront.com') document.title = 'LF-Hotfix only'
    if (webUrl.slice(0, 21) === 'http://localhost:9000') document.title = 'LF-Localhost'
  }, [])

  return (
    <main>
      <Helmet>
        <meta
          httpEquiv='Content-Security-Policy'
          content={`
            default-src 'self' ${trustedSourceList.join(' ')};
            script-src 'self' 'unsafe-eval' ${trustedScriptSrcList.join(' ')};
            img-src 'self' blob: ${trustedImageSrcList.join(' ')};
            connect-src 'self' ${trustedConnectSrcList.join(' ')};
            frame-src 'self' ${trustedFrameSrcList.join(' ')};
            style-src 'unsafe-inline' ${trustedStyleSrcList.join(' ')};
            worker-src 'self' blob:; object-src 'none';
          `}
        />
      </Helmet>
      <GoogleReCaptchaProvider reCaptchaKey={Env.shared.reactAppRecaptchaSiteKey}>
        <Provider store={createAppStore()}>
          <AutoTimeoutTracker />
          <StyleReset />
          <BrowserRouter>
            <ErrorBoundaries>
              <ConnectionNoticeBanner />
              <Loading />
              <Navbar />
              <Routes>
                <Route path='/' element={<LoginPage />} />
                <Route path='/signup' element={<SignupPage />} />
                <Route
                  path='/project'
                  element={
                    <ProtectedRoute>
                      <ProjectLandingPage />
                    </ProtectedRoute>
                  }
                />
                <Route path='/account_setup' element={<AccountSetupPage />} />
                <Route path='/account_setup/:subpagetype' element={<AccountSetupPage />} />
                <Route
                  path='/dashboard/:projectId'
                  element={
                    <ProtectedRoute requireProjectId={true}>
                      <OverviewPageV2 />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path='/project_setup/:projectId'
                  element={
                    <ProtectedRoute requireProjectId={true}>
                      <ProjectSetupPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/project_setup/:projectId/questionnaire/:questionnaireId'
                  element={
                    <ProtectedRoute requireProjectId={true}>
                      <QuestionnairePage viewOnly={false} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/participants/:projectId'
                  element={
                    <ProtectedRoute requireProjectId={true}>
                      <ParticipantsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/adherence/:projectId/'
                  element={
                    <ProtectedRoute requireProjectId={true}>
                      <AdherencePage />
                    </ProtectedRoute>
                  }>
                  <Route path=':adherenceType' element={<AdherenceTableBlock />} />
                </Route>
                <Route
                  path='/settings/:projectId'
                  element={
                    <ProtectedRoute requireProjectId={true}>
                      <ProjectSettingsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/data_visualization/:projectId/:participantId?'
                  element={
                    <ProtectedRoute requireProjectId={true}>
                      <DataVisualizerPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/data_analysis/:analysisType/:projectId'
                  element={
                    <ProtectedRoute requireProjectId={true}>
                      <AnalysisPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/data_visualization_timeseries/:projectId/:participantId?/:initGraphDate?'
                  element={
                    <ProtectedRoute requireProjectId={true}>
                      <DataVisualizerTimeseriesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/data_download/:projectId'
                  element={
                    <ProtectedRoute requireProjectId={true}>
                      <DataDownloadPage />
                    </ProtectedRoute>
                  }
                />
                <Route path='*' element={<PageNotFoundPage />} />
              </Routes>
            </ErrorBoundaries>
          </BrowserRouter>
        </Provider>
      </GoogleReCaptchaProvider>
    </main>
  )
}
